// Agency Compliance - License, E&O, AHIP, carrier appointment matrix
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Shield, ArrowLeft, RefreshCw, CheckCircle, AlertTriangle,
  XCircle, Clock, ChevronRight, Search, FileText
} from 'lucide-react';
import { TourBubble } from '../components/tour/TourOverlay';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');


const COMPLIANCE_TOUR_STEPS = [
  { type: 'splash', title: 'Compliance Center', description: 'Track every agent\'s license, E&O insurance, AHIP certification, and carrier appointments in one place. Never miss an expiration — yellow warnings appear 60 days before.' },
  { target: 'compliance-summary', emoji: '📋', title: 'Compliance Overview', description: 'At-a-glance counts: how many agents are fully compliant (green), approaching expiration (yellow), or expired/missing (red). Focus on red first.', position: 'bottom' },
  { target: 'compliance-search', emoji: '🔍', title: 'Search & Filter', description: 'Search by agent name or filter by status — All, Compliant, Warning, or Expired. Quickly find who needs attention.', position: 'bottom' },
  { target: 'compliance-matrix', emoji: '📊', title: 'Compliance Matrix', description: 'Each row is an agent. Columns show NPN, license status, E&O insurance, AHIP certification, carrier appointment count, and overall status. Dates show when items expire.', position: 'top' },
  { type: 'celebration', title: 'Stay Compliant!', description: 'CMS fines for non-compliant agents can reach $25K per violation. Check this page weekly and address yellow warnings before they turn red.' },
];

// Status cell
function StatusCell({ status, label }) {
  const configs = {
    current: { icon: CheckCircle, bg: 'bg-green-100', text: 'text-green-700', iconColor: 'text-green-500' },
    warning: { icon: AlertTriangle, bg: 'bg-yellow-100', text: 'text-yellow-700', iconColor: 'text-yellow-500' },
    expired: { icon: XCircle, bg: 'bg-red-100', text: 'text-red-700', iconColor: 'text-red-500' },
    missing: { icon: Clock, bg: 'bg-gray-100', text: 'text-gray-500', iconColor: 'text-gray-400' }
  };
  const config = configs[status] || configs.missing;
  const Icon = config.icon;

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon size={12} className={config.iconColor} />
      {label || status}
    </div>
  );
}

// Format date
function formatDate(dateStr) {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AgencyCompliance() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  // Tour state
  const [compTourStep, setCompTourStep] = useState(0);
  const [showCompTour, setShowCompTour] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('agency-compliance-tour-dismissed')) setShowCompTour(true);
  }, []);

  const advanceCompTour = useCallback(() => {
    setCompTourStep(s => {
      if (s + 1 >= COMPLIANCE_TOUR_STEPS.length) {
        localStorage.setItem('agency-compliance-tour-dismissed', 'true');
        setShowCompTour(false);
        return 0;
      }
      return s + 1;
    });
  }, []);
  const backCompTour = useCallback(() => setCompTourStep(s => Math.max(0, s - 1)), []);
  const dismissCompTour = useCallback(() => { localStorage.setItem('agency-compliance-tour-dismissed', 'true'); setShowCompTour(false); }, []);

  useEffect(() => {
    if (!showCompTour) return;
    const h = (e) => {
      if (e.key === 'Escape') dismissCompTour();
      if (e.key === 'ArrowRight' || e.key === 'Enter') advanceCompTour();
      if (e.key === 'ArrowLeft') backCompTour();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [showCompTour, advanceCompTour, backCompTour, dismissCompTour]);

  const fetchCompliance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/agency/compliance/overview`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json);
    } catch (err) {
      console.error('[Compliance] Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCompliance(); }, [fetchCompliance]);

  const filteredAgents = data?.agents?.filter(a => {
    if (search && !a.name?.toLowerCase().includes(search.toLowerCase()) && !a.email?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter !== 'all' && a.status !== filter) return false;
    return true;
  }) || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/agency/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#172A3A] flex items-center gap-2">
                <Shield className="text-blue-600" size={24} />
                Compliance Center
              </h1>
              <p className="text-sm text-gray-500">License, E&O, AHIP, and carrier appointments</p>
            </div>
          </div>
          <button onClick={fetchCompliance} className="p-2 hover:bg-gray-100 rounded-lg">
            <RefreshCw size={18} className="text-gray-500" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Summary Cards */}
        {data?.summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-tour="compliance-summary">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Total Agents</p>
              <p className="text-2xl font-bold text-[#172A3A]">{data.summary.total}</p>
            </div>
            <div className="bg-white rounded-xl border-2 border-green-200 p-4">
              <p className="text-sm text-green-600">Compliant</p>
              <p className="text-2xl font-bold text-green-600">{data.summary.compliant}</p>
            </div>
            <div className="bg-white rounded-xl border-2 border-yellow-200 p-4">
              <p className="text-sm text-yellow-600">Warnings</p>
              <p className="text-2xl font-bold text-yellow-600">{data.summary.warnings}</p>
            </div>
            <div className="bg-white rounded-xl border-2 border-red-200 p-4">
              <p className="text-sm text-red-600">Expired/Missing</p>
              <p className="text-2xl font-bold text-red-600">{data.summary.expired}</p>
            </div>
          </div>
        )}

        {/* Search & Filter */}
        <div className="flex items-center gap-3 flex-wrap" data-tour="compliance-search">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search agents..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {[['all', 'All'], ['compliant', 'Compliant'], ['warning', 'Warning'], ['expired', 'Expired']].map(([val, label]) => (
              <button key={val} onClick={() => setFilter(val)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  filter === val ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'
                }`}>{label}</button>
            ))}
          </div>
        </div>

        {/* Compliance Matrix */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-x-auto" data-tour="compliance-matrix">
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
              <p className="text-gray-500">Loading compliance data...</p>
            </div>
          ) : filteredAgents.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              <Shield size={48} className="mx-auto mb-3 opacity-30" />
              <p>No agents match your filter</p>
            </div>
          ) : (
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Agent</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">NPN</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">License</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">E&O</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">AHIP</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Appointments</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredAgents.map(agent => (
                  <tr key={agent.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-semibold text-gray-900">{agent.name}</p>
                        <p className="text-xs text-gray-400">{agent.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-mono text-gray-600">{agent.npn || '--'}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <StatusCell status={agent.license?.status || 'missing'} />
                        {agent.license?.expiration && (
                          <span className="text-[10px] text-gray-400">{formatDate(agent.license.expiration)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <StatusCell status={agent.eo?.status || 'missing'} />
                        {agent.eo?.expiration && (
                          <span className="text-[10px] text-gray-400">{formatDate(agent.eo.expiration)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <StatusCell status={agent.ahip?.status || 'missing'} />
                        {agent.ahip?.year && (
                          <span className="text-[10px] text-gray-400">{agent.ahip.year}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-medium text-gray-700">
                        {agent.appointments?.length || 0} carriers
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusCell
                        status={agent.status}
                        label={agent.status === 'compliant' ? 'Good' : agent.status === 'warning' ? 'Warning' : 'Action Needed'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCompTour && (
        <TourBubble
          stepData={COMPLIANCE_TOUR_STEPS[compTourStep]}
          stepIndex={compTourStep}
          totalSteps={COMPLIANCE_TOUR_STEPS.length}
          onNext={advanceCompTour}
          onBack={backCompTour}
          onDismiss={dismissCompTour}
          splashIcon={<Shield className="w-10 h-10 text-blue-600" />}
          maskId="agency-compliance-tour-mask"
        />
      )}
    </div>
  );
}
